import React from "react";
import { useQuery } from "@apollo/client";
import moment from "moment-timezone";
import sortBy from "lodash/sortBy";

import DocsSnippet from "components/DocsSnippet";
import Panel from "components/Panel";
import PanelTable from "components/PanelTable";
import PanelSection from "components/PanelSection";
import SQL from "components/SQL";

import QUERY from "./Query.graphql";
import styles from "./style.module.scss";
import {
  BackendDetailsWaitEvents,
  BackendDetailsWaitEventsVariables,
} from "./types/BackendDetailsWaitEvents";
import Loading from "components/Loading";

type BackendSnapshotType = {
  id: string;
  collectedAt: number;
  waitEvent: WaitEvent;
  waitEventType: WaitEventType;
  queryTextShort: string | null;
};

type Props = {
  databaseId: string;
  backendLocator: string;
};

const eventTypeDocTexts = {
  Lock:
    "The server process is waiting for a heavyweight lock. Heavyweight locks, also known as lock manager locks or simply locks, " +
    "primarily protect SQL-visible objects such as tables. However, they are also used to ensure mutual exclusion for certain " +
    "internal operations such as relation extension. Event identifies the type of lock awaited.",
  BufferPin:
    "The server process is waiting for exclusive access to a data buffer. " +
    "Buffer pin waits can be protracted if another process holds an open cursor that last read data from the buffer in question.",
  LWLock:
    "The server process is waiting for a lightweight lock. Most such locks protect a particular data structure " +
    "in shared memory. Event contains a name identifying the purpose of the lightweight lock. " +
    "(Some locks have specific names; others are part of a group of locks each with a similar purpose.)",
  Activity:
    "The server process is idle. This event type indicates a process waiting for activity in its main processing loop. " +
    "Event identifies the specific wait point.",
  Client:
    "The server process is waiting for activity on a socket connected to a user application. " +
    "Thus, the server expects something to happen that is independent of its internal processes. " +
    "Event identifies the specific wait point.",
  Extension:
    "The server process is waiting for some condition defined by an extension module.",
  IPC:
    "The server process is waiting for some interaction with another server process. " +
    "Event identifies the specific wait point.",
  Timeout:
    "The server process is waiting for a timeout to expire. Event identifies the specific wait point.",
  IO: "The server process is waiting for an I/O operation to complete. Event identifies the specific wait point.",
  InjectionPoint:
    "The server process is waiting for an injection point to reach an outcome defined in a test. This type has no predefined wait points.",
} as const;

type WaitEventType = keyof typeof eventTypeDocTexts;

const eventDocTexts = {
  /* LWLock */
  ShmemIndex: "Waiting to find or allocate space in shared memory.",
  OidGen: "Waiting to allocate a new OID.",
  XidGen: "Waiting to allocate a new transaction ID.",
  ProcArray:
    "Waiting to access the shared per-process data structures " +
    "(typically, to get a snapshot or report a session's transaction ID).",
  SInvalRead:
    "Waiting to retrieve messages from the shared catalog invalidation queue.",
  SInvalWrite:
    "Waiting to add a message to the shared catalog invalidation queue.",
  WALBufMapping: "Waiting to replace a page in WAL buffers.",
  WALWrite: "Waiting for WAL buffers to be written to disk.",
  ControlFile:
    "Waiting to read or update the pg_control file or create a new WAL file.",
  CheckpointLock: "Waiting to begin a checkpoint.",
  XactSLRU: "Waiting to access the transaction status SLRU cache.",
  SubtransSLRU: "Waiting to access the sub-transaction SLRU cache.",
  MultiXactGen: "Waiting to read or update shared multixact state.",
  MultiXactOffsetSLRU: "Waiting to access the multixact offset SLRU cache.",
  MultiXactMemberSLRU: "Waiting to access the multixact member SLRU cache.",
  RelCacheInit:
    "Waiting to read or update a pg_internal.init relation cache initialization file.",
  CheckpointerComm: "Waiting to manage fsync requests.",
  TwoPhaseState:
    "Waiting to read or update the state of prepared transactions.",
  TablespaceCreate: "Waiting to create or drop a tablespace.",
  BtreeVacuum:
    "Waiting to read or update vacuum-related information for a B-tree index.",
  AddinShmemInit:
    "Waiting to manage an extension's space allocation in shared memory.",
  Autovacuum:
    "Waiting to read or update the current state of autovacuum workers.",
  AutovacuumSchedule:
    "Waiting to ensure that a table selected for autovacuum still needs vacuuming.",
  SyncScan:
    "Waiting to select the starting location of a synchronized table scan.",
  RelationMapping:
    "Waiting to read or update a pg_filenode.map file " +
    "(used to track the filenode assignments of certain system catalogs).",
  NotifySLRU: "Waiting to access the NOTIFY message SLRU cache.",
  NotifyQueue: "Waiting to read or update NOTIFY messages.",
  SerializableXactHash:
    "Waiting to read or update information about serializable transactions.",
  SerializableFinishedList:
    "Waiting to access the list of finished serializable transactions.",
  SerializablePredicateList:
    "Waiting to access the list of predicate locks held by serializable transactions.",
  SerialSLRU:
    "Waiting to access the serializable transaction conflict SLRU cache.",
  SyncRep:
    "Waiting to read or update information about the state of synchronous replication.",
  BackgroundWorker: "Waiting to read or update background worker state.",
  DynamicSharedMemoryControl:
    "Waiting to read or update dynamic shared memory allocation information.",
  AutoFile: "Waiting to update the postgresql.auto.conf file.",
  ReplicationSlotAllocation: "Waiting to allocate or free a replication slot.",
  ReplicationSlotControl: "Waiting to read or update replication slot state.",
  CommitTsSLRU: "Waiting to access the commit timestamp SLRU cache.",
  CommitTs:
    "Waiting to read or update the last value set for a transaction commit timestamp.",
  ReplicationOrigin: "Waiting to create, drop or use a replication origin.",
  MultiXactTruncation: "Waiting to read or truncate multixact information.",
  OldSnapshotTimeMapLock:
    "Waiting to read or update old snapshot control information.",
  BackendRandomLock: "Waiting to generate a random number.",
  LogicalRepWorker:
    "Waiting to read or update the state of logical replication workers.",
  XactTruncation:
    "Waiting to execute pg_xact_status or update the oldest transaction ID available to it.",
  WrapLimitsVacuum:
    "Waiting to update limits on transaction id and multixact consumption.",
  NotifyQueueTail: "Waiting to update limit on NOTIFY message storage.",
  WaitEventCustom: "Waiting to read or update custom wait events information.",
  WALSummarizer: "Waiting to read or update WAL summarization state.",
  DSMRegistry: "Waiting to read or update the dynamic shared memory registry.",
  InjectionPoint:
    "Waiting to read or update information related to injection points.",
  SerialControl: "Waiting to read or update shared pg_serial state.",
  AioWorkerSubmissionQueue: "Waiting to access AIO worker submission queue.",
  /* LWLock (LWTRANCHE) */
  XactBuffer: "Waiting for I/O on a transaction status SLRU buffer.",
  CommitTsBuffer: "Waiting for I/O on a commit timestamp SLRU buffer.",
  SubtransBuffer: "Waiting for I/O on a sub-transaction SLRU buffer.",
  MultiXactOffsetBuffer: "Waiting for I/O on a multixact offset SLRU buffer.",
  MultiXactMemberBuffer: "Waiting for I/O on a multixact member SLRU buffer.",
  NotifyBuffer: "Waiting for I/O on a NOTIFY message SLRU buffer.",
  SerialBuffer:
    "Waiting for I/O on a serializable transaction conflict SLRU buffer.",
  WALInsert: "Waiting to insert WAL data into a memory buffer.",
  BufferContent: "Waiting to access a data page in memory.",
  BufferIo: "Waiting for I/O on a data page.",
  ReplicationOriginState:
    "Waiting to read or update the progress of one replication origin.",
  ReplicationSlotIO: "Waiting for I/O on a replication slot.",
  LockFastPath:
    "Waiting to read or update a process' fast-path lock information.",
  BufferMapping:
    "Waiting to associate a data block with a buffer in the buffer pool.",
  LockManager: "Waiting to read or update information about heavyweight locks.",
  PredicateLockManager:
    "Waiting to access predicate lock information used by serializable transactions.",
  ParallelHashJoin:
    "Waiting to synchronize workers during Parallel Hash Join plan execution.",
  ParallelQueryDSA:
    "Waiting for parallel query dynamic shared memory allocation.",
  PerSessionDSA: "Waiting for parallel query dynamic shared memory allocation.",
  PerSessionRecordType:
    "Waiting to access a parallel query's information about composite types.",
  PerSessionRecordTypmod:
    "Waiting to access a parallel query's information about type modifiers that identify anonymous record types.",
  SharedTupleStore:
    "Waiting to access a shared tuple store during parallel query.",
  SharedTidBitmap:
    "Waiting to access a shared TID bitmap during a parallel bitmap index scan.",
  ParallelAppend:
    "Waiting to choose the next subplan during Parallel Append plan execution.",
  PerXactPredicateList:
    "Waiting to access the list of predicate locks held by the current serializable transaction during a parallel query.",
  ParallelBtreeScan:
    "Waiting to synchronize workers during Parallel B-tree scan plan execution.",
  PgStatsDSA: "Waiting for stats dynamic shared memory allocator access.",
  PgStatsHash: "Waiting for stats shared memory hash table access.",
  PgStatsData: "Waiting for shared memory stats data access.",
  LogicalRepLauncherDSA:
    "Waiting to access logical replication launcher's dynamic shared memory allocator.",
  LogicalRepLauncherHash:
    "Waiting to access logical replication launcher's shared hash table.",
  DSMRegistryDSA:
    "Waiting to access dynamic shared memory registry's dynamic shared memory allocator.",
  DSMRegistryHash:
    "Waiting to access dynamic shared memory registry's shared hash table.",
  ParallelVacuumDSA:
    "Waiting for parallel vacuum dynamic shared memory allocation.",
  AioUringCompletion:
    "Waiting for another process to complete IO via io_uring.",
  /* Lock */
  relation: "Waiting to acquire a lock on a relation.",
  extend: "Waiting to extend a relation.",
  page: "Waiting to acquire a lock on a page of a relation.",
  tuple: "Waiting to acquire a lock on a tuple.",
  transactionid: "Waiting for a transaction to finish.",
  virtualxid: "Waiting to acquire a virtual transaction ID lock.",
  spectoken: "Waiting to acquire a speculative insertion lock.",
  object: "Waiting to acquire a lock on a non-relation database object.",
  userlock: "Waiting to acquire a user lock.",
  advisory: "Waiting to acquire an advisory user lock.",
  frozenid:
    "Waiting to update pg_database.datfrozenxid and pg_database.datminmxid.",
  applytransaction:
    "Waiting to acquire a lock on a remote transaction being applied by a logical replication subscriber.",
  /* BufferPin */
  BufferPin: "Waiting to acquire an exclusive pin on a buffer.",
  /* Extension */
  Extension: "Waiting in an extension.",
  /* Activity */
  ArchiverMain: "Waiting in main loop of archiver process.",
  AutovacuumMain: "Waiting in main loop of autovacuum launcher process.",
  BgwriterHibernate: "Waiting in background writer process, hibernating.",
  BgwriterMain: "Waiting in main loop of background writer process.",
  CheckpointerMain: "Waiting in main loop of checkpointer process.",
  LogicalApplyMain:
    "Waiting in main loop of logical replication apply process.",
  LogicalLauncherMain:
    "Waiting in main loop of logical replication launcher process.",
  PgStatMain: "Waiting in main loop of statistics collector process.",
  RecoveryWalAll:
    "Waiting in main loop of startup process for WAL to arrive, during streaming recovery.",
  RecoveryWalStream:
    "Waiting in main loop of startup process for WAL to arrive, during streaming recovery.",
  SysloggerMain: "Waiting in main loop of syslogger process.",
  WalReceiverMain: "Waiting in main loop of WAL receiver process.",
  WalSenderMain: "Waiting in main loop of WAL sender process.",
  WalWriterMain: "Waiting in main loop of WAL writer process.",
  CheckpointerShutdown: "Waiting for checkpointer process to be terminated.",
  IoWorkerMain: "Waiting in main loop of IO Worker process.",
  LogicalParallelApplyMain:
    "Waiting in main loop of logical replication parallel apply process.",
  ReplicationSlotsyncMain: "Waiting in main loop of slot sync worker.",
  ReplicationSlotsyncShutdown: "Waiting for slot sync worker to shut down.",
  WalSummarizerWal: "Waiting in WAL summarizer for more WAL to be generated.",
  /* Client */
  ClientRead: "Waiting to read data from the client.",
  ClientWrite: "Waiting to write data to the client.",
  LibpqwalreceiverConnect:
    "Waiting in WAL receiver to establish connection to remote server.",
  LibpqwalreceiverReceive:
    "Waiting in WAL receiver to receive data from remote server.",
  SslOpenServer: "Waiting for SSL while attempting connection.",
  WalReceiverWaitStart:
    "Waiting for startup process to send initial data for streaming replication.",
  WalSenderWaitForWal: "Waiting for WAL to be flushed in WAL sender process.",
  WalSenderWriteData:
    "Waiting for any activity when processing replies from WAL receiver in WAL sender process.",
  GssOpenServer:
    "Waiting to read data from the client while establishing a GSSAPI session.",
  WaitForStandbyConfirmation:
    "Waiting for WAL to be received and flushed by the physical standby.",
  /* IPC */
  BgworkerShutdown: "Waiting for background worker to shut down.",
  BgworkerStartup: "Waiting for background worker to start up.",
  BtreePage:
    "Waiting for the page number needed to continue a parallel B-tree scan to become available.",
  XactGroupUpdate:
    "Waiting for the group leader to update transaction status at transaction end.",
  ExecuteGather:
    "Waiting for activity from a child process while executing a Gather plan node.",
  HashBatchAllocate:
    "Waiting for an elected Parallel Hash participant to allocate a hash table.",
  HashBatchElect:
    "Waiting to elect a Parallel Hash participant to allocate a hash table.",
  HashBatchLoad:
    "Waiting for other Parallel Hash participants to finish loading a hash table.",
  HashBuildAllocate:
    "Waiting for an elected Parallel Hash participant to allocate the initial hash table.",
  HashBuildElect:
    "Waiting to elect a Parallel Hash participant to allocate the initial hash table.",
  HashBuildHashInner:
    "Waiting for other Parallel Hash participants to finish hashing the inner relation.",
  HashBuildHashOuter:
    "Waiting for other Parallel Hash participants to finish partitioning the outer relation.",
  HashGrowBatchesAllocate:
    "Waiting for an elected Parallel Hash participant to allocate more batches.",
  HashGrowBatchesDecide:
    "Waiting to elect a Parallel Hash participant to decide on future batch growth.",
  HashGrowBatchesElect:
    "Waiting to elect a Parallel Hash participant to allocate more batches.",
  HashGrowBatchesFinish:
    "Waiting for an elected Parallel Hash participant to decide on future batch growth.",
  HashGrowBatchesRepartition:
    "Waiting for other Parallel Hash participants to finish repartitioning.",
  HashGrowBucketsAllocate:
    "Waiting for an elected Parallel Hash participant to finish allocating more buckets.",
  HashGrowBucketsElect:
    "Waiting to elect a Parallel Hash participant to allocate more buckets.",
  HashGrowBucketsReinsert:
    "Waiting for other Parallel Hash participants to finish inserting tuples into new buckets.",
  LogicalSyncData:
    "Waiting for a logical replication remote server to send data for initial table synchronization.",
  LogicalSyncStateChange:
    "Waiting for a logical replication remote server to change state.",
  MessageQueueInternal:
    "Waiting for another process to be attached to a shared message queue.",
  MessageQueuePutMessage:
    "Waiting to write a protocol message to a shared message queue.",
  MessageQueueReceive: "Waiting to receive bytes from a shared message queue.",
  MessageQueueSend: "Waiting to send bytes to a shared message queue.",
  ParallelBitmapScan: "Waiting for parallel bitmap scan to become initialized.",
  ParallelCreateIndexScan:
    "Waiting for parallel CREATE INDEX workers to finish heap scan.",
  ParallelFinish: "Waiting for parallel workers to finish computing.",
  ProcarrayGroupUpdate:
    "Waiting for the group leader to clear the transaction ID at transaction end.",
  Promote: "Waiting for standby promotion.",
  ReplicationOriginDrop:
    "Waiting for a replication origin to become inactive so it can be dropped.",
  ReplicationSlotDrop:
    "Waiting for a replication slot to become inactive so it can be dropped.",
  SafeSnapshot:
    "Waiting to obtain a valid snapshot for a READ ONLY DEFERRABLE transaction.",
  // Note: there should be the following SyncRep belonging to IPC,
  // but we can't define here because the same name wait event exists in LWLock.
  // This means the customer may see the incorrect description.
  // SyncRep:
  //   "Waiting for confirmation from a remote server during synchronous replication.",
  CheckpointDone: "Waiting for a checkpoint to complete.",
  CheckpointStart: "Waiting for a checkpoint to start.",
  AppendReady: "Waiting for subplan nodes of an Append plan node to be ready.",
  BackendTermination: "Waiting for the termination of another backend.",
  BackupWaitWalArchive:
    "Waiting for WAL files required for a backup to be successfully archived.",
  RecoveryConflictSnapshot:
    "Waiting for recovery conflict resolution for a vacuum cleanup.",
  RecoveryConflictTablespace:
    "Waiting for recovery conflict resolution for dropping a tablespace.",
  RecoveryPause: "Waiting for recovery to be resumed.",
  ArchiveCleanupCommand: "Waiting for archive_cleanup_command to complete.",
  ArchiveCommand: "Waiting for archive_command to complete.",
  CheckpointDelayComplete:
    "Waiting for a backend that blocks a checkpoint from completing.",
  CheckpointDelayStart:
    "Waiting for a backend that blocks a checkpoint from starting.",
  HashGrowBatchesReallocate:
    "Waiting for an elected Parallel Hash participant to allocate more batches.",
  HashGrowBucketsReallocate:
    "Waiting for an elected Parallel Hash participant to finish allocating more buckets.",
  LogicalApplySendData:
    "Waiting for a logical replication leader apply process to send data to a parallel apply process.",
  LogicalParallelApplyStateChange:
    "Waiting for a logical replication parallel apply process to change state.",
  MultixactCreation: "Waiting for a multixact creation to complete.",
  RecoveryEndCommand: "Waiting for recovery_end_command to complete.",
  RestoreCommand: "Waiting for restore_command to complete.",
  WalReceiverExit: "Waiting for the WAL receiver to exit.",
  WalSummaryReady: "Waiting for a new WAL summary to be generated.",
  /* Timeout */
  BaseBackupThrottle: "Waiting during base backup when throttling activity.",
  PgSleep: "Waiting due to a call to pg_sleep or a sibling function.",
  RecoveryApplyDelay:
    "Waiting to apply WAL during recovery because of a delay setting.",
  RecoveryRetrieveRetryInterval:
    "Waiting during recovery when WAL data is not available from any source (pg_wal, archive or stream).",
  RegisterSyncRequest:
    "Waiting while sending synchronization requests to the checkpointer, because the request queue is full.",
  VacuumDelay: "Waiting in a cost-based vacuum delay point.",
  CheckpointWriteDelay: "Waiting between writes while performing a checkpoint.",
  SpinDelay: "Waiting while acquiring a contended spinlock.",
  VacuumTruncate:
    "Waiting to acquire an exclusive lock to truncate off any empty pages at the end of a table vacuumed.",
  WalSummarizerError: "Waiting after a WAL summarizer error.",
  /* IO */
  BuffileRead: "Waiting for a read from a buffered file.",
  BuffileWrite: "Waiting for a write to a buffered file.",
  ControlFileRead: "Waiting for a read from the pg_control file.",
  ControlFileSync: "Waiting for the pg_control file to reach durable storage.",
  ControlFileSyncUpdate:
    "Waiting for an update to the pg_control file to reach durable storage.",
  ControlFileWrite: "Waiting for a write to the pg_control file.",
  ControlFileWriteUpdate: "Waiting for a write to update the pg_control file.",
  CopyFileRead: "Waiting for a read during a file copy operation.",
  CopyFileWrite: "Waiting for a write during a file copy operation.",
  DataFileExtend: "Waiting for a relation data file to be extended.",
  DataFileFlush: "Waiting for a relation data file to reach durable storage.",
  DataFileImmediateSync:
    "Waiting for an immediate synchronization of a relation data file to durable storage.",
  DataFilePrefetch:
    "Waiting for an asynchronous prefetch from a relation data file.",
  DataFileRead: "Waiting for a read from a relation data file.",
  DataFileSync:
    "Waiting for changes to a relation data file to reach durable storage.",
  DataFileTruncate: "Waiting for a relation data file to be truncated.",
  DataFileWrite: "Waiting for a write to a relation data file.",
  DsmFillZeroWrite:
    "Waiting to fill a dynamic shared memory backing file with zeroes.",
  LockFileAddtodatadirRead:
    "Waiting for a read while adding a line to the data directory lock file.",
  LockFileAddtodatadirSync:
    "Waiting for data to reach durable storage while adding a line to the data directory lock file.",
  LockFileAddtodatadirWrite:
    "Waiting for a write while adding a line to the data directory lock file.",
  LockFileCreateRead:
    "Waiting to read while creating the data directory lock file.",
  LockFileCreateSync:
    "Waiting for data to reach durable storage while creating the data directory lock file.",
  LockFileCreateWrite:
    "Waiting for a write while creating the data directory lock file.",
  LockFileRecheckdatadirRead:
    "Waiting for a read during recheck of the data directory lock file.",
  LogicalRewriteCheckpointSync:
    "Waiting for logical rewrite mappings to reach durable storage during a checkpoint.",
  LogicalRewriteMappingSync:
    "Waiting for mapping data to reach durable storage during a logical rewrite.",
  LogicalRewriteMappingWrite:
    "Waiting for a write of mapping data during a logical rewrite.",
  LogicalRewriteSync:
    "Waiting for logical rewrite mappings to reach durable storage.",
  LogicalRewriteTruncate:
    "Waiting for truncate of mapping data during a logical rewrite.",
  LogicalRewriteWrite: "Waiting for a write of logical rewrite mappings.",
  RelationMapRead: "Waiting for a read of the relation map file.",
  RelationMapSync:
    "Waiting for the relation map file to reach durable storage.",
  RelationMapWrite: "Waiting for a write to the relation map file.",
  ReorderBufferRead: "Waiting for a read during reorder buffer management.",
  ReorderBufferWrite: "Waiting for a write during reorder buffer management.",
  ReorderLogicalMappingRead:
    "Waiting for a read of a logical mapping during reorder buffer management.",
  ReplicationSlotRead:
    "Waiting for a read from a replication slot control file.",
  ReplicationSlotRestoreSync:
    "Waiting for a replication slot control file to reach durable storage while restoring it to memory.",
  ReplicationSlotSync:
    "Waiting for a replication slot control file to reach durable storage.",
  ReplicationSlotWrite:
    "Waiting for a write to a replication slot control file.",
  SlruFlushSync:
    "Waiting for SLRU data to reach durable storage during a checkpoint or database shutdown.",
  SlruRead: "Waiting for a read of an SLRU page.",
  SlruSync:
    "Waiting for SLRU data to reach durable storage following a page write.",
  SlruWrite: "Waiting for a write of an SLRU page.",
  SnapbuildRead:
    "Waiting for a read of a serialized historical catalog snapshot.",
  SnapbuildSync:
    "Waiting for a serialized historical catalog snapshot to reach durable storage.",
  SnapbuildWrite:
    "Waiting for a write of a serialized historical catalog snapshot.",
  TimelineHistoryFileSync:
    "Waiting for a timeline history file received via streaming replication to reach durable storage.",
  TimelineHistoryFileWrite:
    "Waiting for a write of a timeline history file received via streaming replication.",
  TimelineHistoryRead: "Waiting for a read of a timeline history file.",
  TimelineHistorySync:
    "Waiting for a newly created timeline history file to reach durable storage.",
  TimelineHistoryWrite:
    "Waiting for a write of a newly created timeline history file.",
  TwophaseFileRead: "Waiting for a read of a two phase state file.",
  TwophaseFileSync:
    "Waiting for a two phase state file to reach durable storage.",
  TwophaseFileWrite: "Waiting for a write of a two phase state file.",
  WalsenderTimelineHistoryRead:
    "Waiting for a read from a timeline history file during a walsender timeline command.",
  WalBootstrapSync:
    "Waiting for WAL to reach durable storage during bootstrapping.",
  WalBootstrapWrite: "Waiting for a write of a WAL page during bootstrapping.",
  WalCopyRead:
    "Waiting for a read when creating a new WAL segment by copying an existing one.",
  WalCopySync:
    "Waiting for a new WAL segment created by copying an existing one to reach durable storage.",
  WalCopyWrite:
    "Waiting for a write when creating a new WAL segment by copying an existing one.",
  WalInitSync:
    "Waiting for a newly initialized WAL file to reach durable storage.",
  WalInitWrite: "Waiting for a write while initializing a new WAL file.",
  WalRead: "Waiting for a read from a WAL file.",
  WalSync: "Waiting for a WAL file to reach durable storage.",
  WalSyncMethodAssign:
    "Waiting for data to reach durable storage while assigning a new WAL sync method.",
  WalWrite: "Waiting for a write to a WAL file.",
  ProcSignalBarrier:
    "Waiting for a barrier event to be processed by all backends.",
  AioIoCompletion: "Waiting for another process to complete IO.",
  AioIoUringSubmit: "Waiting for IO submission via io_uring.",
  AioIoUringExecution: "Waiting for IO execution via io_uring.",
  BasebackupRead: "Waiting for base backup to read from a file.",
  BasebackupSync:
    "Waiting for data written by a base backup to reach durable storage.",
  BasebackupWrite: "Waiting for base backup to write to a file.",
  BuffileTruncate: "Waiting for a buffered file to be truncated.",
  CopyFileCopy: "Waiting for a file copy operation.",
  DsmAllocate: "Waiting for a dynamic shared memory segment to be allocated.",
  RelationMapReplace: "Waiting for durable replacement of a relation map file.",
  VersionFileSync:
    "Waiting for the version file to reach durable storage while creating a database.",
  VersionFileWrite:
    "Waiting for the version file to be written while creating a database.",
  WalSummaryRead: "Waiting for a read from a WAL summary file.",
  WalSummaryWrite: "Waiting for a write to a WAL summary file.",
};

type WaitEvent = keyof typeof eventDocTexts;

const WaitEvents: React.FunctionComponent<Props> = ({
  databaseId,
  backendLocator,
}) => {
  const { data, loading, error } = useQuery<
    BackendDetailsWaitEvents,
    BackendDetailsWaitEventsVariables
  >(QUERY, {
    variables: {
      databaseId,
      backendLocator,
    },
  });
  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const snapshots = sortBy(
    data.getBackendDetails.snapshots as BackendSnapshotType[],
    (s: BackendSnapshotType): number => -s.collectedAt,
  );

  return (
    <Panel title="Wait Events">
      <PanelTable borders>
        <thead>
          <tr>
            <th>Seen At</th>
            <th>Event Type</th>
            <th>Event</th>
            <th>Query</th>
          </tr>
        </thead>
        <tbody>
          {snapshots.map((s: BackendSnapshotType) => (
            <WaitEventSnapshot snapshot={s} key={s.id} />
          ))}
        </tbody>
      </PanelTable>
      <PanelSection>
        <strong>Hint:</strong> Wait events are sampled every 10 seconds
      </PanelSection>
    </Panel>
  );
};

const WaitEventSnapshot: React.FunctionComponent<{
  snapshot: BackendSnapshotType;
}> = ({ snapshot }) => {
  return (
    <tr>
      <td>{moment.unix(snapshot.collectedAt).format("ll LTS z")}</td>
      <td>
        {snapshot.waitEventType}
        {eventTypeDocTexts[snapshot.waitEventType] && (
          <span className={styles.docSnippet}>
            <DocsSnippet
              href="/docs/wait_events"
              title={snapshot.waitEventType}
              content={eventTypeDocTexts[snapshot.waitEventType]}
            />
          </span>
        )}
      </td>
      <td>
        {snapshot.waitEvent}
        {eventDocTexts[snapshot.waitEvent] && (
          <span className={styles.docSnippet}>
            <DocsSnippet
              href="/docs/wait_events"
              title={snapshot.waitEvent}
              content={eventDocTexts[snapshot.waitEvent]}
            />
          </span>
        )}
      </td>
      <td>
        {(snapshot.queryTextShort && (
          <SQL inline sql={snapshot.queryTextShort} />
        )) ||
          "-"}
      </td>
    </tr>
  );
};

export default WaitEvents;
